import React, {
  FunctionComponent,
  MouseEvent,
  useCallback,
  useContext,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import find from 'lodash/find';
import get from 'lodash/get';
import last from 'lodash/last';
import split from 'lodash/split';

import { globalTheme } from '../../../../shared/theme/theme';
import { useAppSelector } from '../../../../store/store';
import { ROOT } from '../../../../store/utils/files-names';
import { ChangeDetectorContext } from '../change-detector-context/ChangeDetectorContext';
import { ChangeDetectorContextModel } from '../change-detector-context/models/change-detector-context.model';
import { ModalContext } from '../modal/ModalContext';
import ModalDialog from '../modal/ModalDialog';
import { ModalContextModel } from '../modal/models/modal-context.model';
import Tab from '../ui/Tab';
import Tabs from '../ui/Tabs';
import { NavTabProps } from './models/nav-tab-props.model';

const NavTab: FunctionComponent<NavTabProps> = ({ tabs }: NavTabProps) => {
  const modalCtx = useContext(ModalContext) as ModalContextModel;
  const cdContext = useContext(
    ChangeDetectorContext
  ) as ChangeDetectorContextModel;
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = useAppSelector(state => get(state, ROOT).isLoading);
  const pathValue = last(split(location.pathname, '/'));
  const renderTabs = !!find(tabs, {
    value: pathValue,
  });

  const tabNavigate = (path: string): void => {
    navigate(`./${path}`);
  };

  const tabNavigateWithPrompt = (path: string): void => {
    modalCtx.componentHandler(
      <ModalDialog
        content={<span>Filled information will be lost. Leave anyway?</span>}
        title={<span>Warning</span>}
        extraButtons={[
          <Button
            onClick={() => {
              navigate(`./${path}`);
              cdContext.setIsChanged(false);
              modalCtx.modalStateHandler();
            }}
            className="dialog-btn dialog-btn-yes"
            key={'prompt-button'}
          >
            <Typography className="dialog-btn-yes-text">Yes</Typography>
          </Button>,
        ]}
      />
    );

    modalCtx.modalStateHandler();
  };

  const tabAction = useCallback<() => (path: string) => void>(
    () => (cdContext.isChanged ? tabNavigateWithPrompt : tabNavigate),
    [cdContext.isChanged]
  );

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {renderTabs && (
        <Tabs
          value={pathValue}
          sx={{
            boxShadow: 'none',
          }}
          TabIndicatorProps={{
            style: {
              background: globalTheme.palette.primary.main,
              height: '4px',
            },
          }}
        >
          {tabs.map(tab => {
            return (
              <Tab
                key={tab.label}
                label={tab.label}
                value={tab.value}
                to={tab.to}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  const tabNavigationFunc = tabAction();
                  tabNavigationFunc(tab.to);
                }}
                component={Link}
                sx={{
                  maxWidth: 'unset',
                  fontWeight: 300,
                  borderBottom: 1.44,
                  borderColor: theme => theme.palette.appGrey.greyPrimary,
                  textTransform: 'capitalize',
                  color: theme => `${theme.palette.black.main} !important`,
                  fontSize: '24px',
                  ...(isLoading && {
                    pointerEvents: 'none',
                  }),
                  ...(tab.sx ?? {}),
                }}
              />
            );
          })}
        </Tabs>
      )}
    </Box>
  );
};

export default NavTab;
